import { Component, computed, effect, inject, OnDestroy, signal, untracked, viewChild } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTitle,
  IonToggle,
  IonToolbar,
  ModalController,
  ModalOptions,
} from '@ionic/angular/standalone';
import {
  withGetProductById,
  withProductDelete,
  withProductUpdate,
} from '@libs/mtjr/shared/generated/entities/product/product-store-features';

import { NgStyle } from '@angular/common';
import { AttributesModalComponent } from '@apps/mtjr/vendors/app/product/attributes/attributes-sheet-component';
import { AttributesPickerStore } from '@apps/mtjr/vendors/app/product/attributes/attributes.store';
import { VariantsModalComponent } from '@apps/mtjr/vendors/app/product/attributes/variations-sheet.component';
import { VendorStore } from '@apps/mtjr/vendors/app/vendor/list/vendors.page';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextAreaFieldComponent, VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { dataFromState, errorState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { VxAttachmentFieldComponent } from '@libs/mtjr/shared/attachment/attachment-field.component';
import { ProductUpdateDtoMap } from '@libs/mtjr/shared/generated/dtoMaps/product-update.dto.map';
import { productUpdateUtils } from '@libs/mtjr/shared/generated/forms';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { plainToInstance } from 'class-transformer';
import { createPatch } from 'rfc6902';
import { Subscription } from 'rxjs';
export const ProductEditStore = signalStore(
  withGetProductById(),
  withProductDelete(),
  withProductUpdate(),
  withMethods((store) => ({
    setProductUpdateFailure(failure: Failure) {
      patchState(store, { productUpdateState: errorState(failure) });
    },
  })),
  withComputed((store) => ({
    product: dataFromState(store.getProductByIdState),
  })),
  withComputed((store) => {
    const createProductDto = () =>
      computed(() => {
        const product = store.product();
        if (!product) return;
        return plainToInstance(ProductUpdateDtoMap, structuredClone(product), {
          excludeExtraneousValues: true,
        });
      });

    return {
      productUpdateForForm: createProductDto(),
      productUpdateForDiff: createProductDto(),
    };
  }),
);
@Component({
  selector: 'mav-product-edit',
  templateUrl: './product-edit.page.html',
  providers: [ProductEditStore],
  imports: [
    IonToggle,
    IonItem,
    IonChip,
    IonLabel,
    IonButton,
    IonText,
    IonTitle,
    IonBackButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    IonContent,
    IonIcon,
    ReactiveFormsModule,
    FormsModule,
    VxTextFieldComponent,
    VxTextAreaFieldComponent,
    VxSubmitButtonComponent,
    VxErrorBarComponent,
    TranslatePipe,
    NgStyle,
    VxAttachmentFieldComponent,
    VxLoadingComponent,
    IonRadio,
    IonRadioGroup,
  ],
})
export class ProductEditPage implements OnDestroy {
  readonly route = inject(ActivatedRoute);
  readonly store = inject(ProductEditStore);
  readonly vendorStore = inject(VendorStore);
  readonly attributesStore = new AttributesPickerStore();
  readonly router = inject(Router);
  readonly modalCtr = inject(ModalController);
  readonly editUtils = productUpdateUtils(inject(NonNullableFormBuilder));
  readonly eventBus = inject(NgEventBus);
  readonly alerts = inject(AppAlerts);
  readonly attachment = viewChild(VxAttachmentFieldComponent);

  readonly productId: string = this.route.snapshot.params['productId'];
  readonly vendorId: string = this.route.snapshot.params['vendorId'];
  readonly formControls = this.editUtils.form.controls;
  readonly advancedMode = signal(false);
  readonly imagePickerOpen = signal(false);
  translateService = inject(TranslateService);
  translate = inject(TranslateService);

  private routeSub?: Subscription;

  constructor() {
    if (this.productId) {
      this.store.getProductById({ id: this.productId });
    }

    this.setupValidators();
    this.setupEffects();
    this.setupRouteSubscription();
    effect(() => {
      const state = this.store.productDeleteState();
      if (state.success) {
        untracked(() => {
          this.alerts.successSnackbarTR('PRODUCTS.PRODUCT_DELETE_SUCCESS');
          this.router.navigate(['main', 'vendors', this.vendorId]);
          this.eventBus.cast('products:fetch', { vendorId: this.vendorId });
        });
      }
    });
  }

  async deleteProduct() {
    const confirm = await this.alerts.yesNoAlert({
      header: `${this.translate.instant('COMMON.DELETE')} (${this.store.product()?.name})`,
      message: this.translateService.instant('PRODUCTS.PRODUCT_DELETE_CONFIRM'),
      yesCaption: this.translateService.instant('PRODUCTS.PRODUCT_DELETE'),
      noCaption: this.translateService.instant('COMMON.CANCEL'),
    });

    if (confirm) {
      await this.store.productDelete({ productId: this.productId });
    }
  }

  removeImage(url: string) {
    const images = this.editUtils.form.controls.images.value ?? [];
    const index = images.indexOf(url);
    if (index === -1) return;
    const newImages = [...images];
    newImages.splice(index, 1);
    this.editUtils.form.controls.images.setValue(newImages);
    this.editUtils.form.controls.images.markAsDirty();
  }

  private setupValidators() {
    this.formControls.price.addValidators(Validators.required);
    this.formControls.stock.addValidators(Validators.required);
  }

  private setupEffects() {
    effect(() => {
      const hasVariants = this.attributesStore.variants().length > 0;
      this.toggleRequiredValidators(['price', 'stock'], !hasVariants);
    });

    effect(() => {
      if (this.store.productUpdateState().success) {
        this.handleSuccessfulUpdate();
      }
    });

    effect(() => {
      const product = this.store.productUpdateForForm();
      if (!product) return;
      this.updateFormWithProduct(product);
    });
  }

  private setupRouteSubscription() {
    this.routeSub = this.route.queryParams.subscribe(async (params) => {
      const activeModal = await this.modalCtr.getTop();
      if (activeModal) return;

      if (params[AttributesModalComponent.modalId]) {
        await this.updateAttributes();
      } else if (params[VariantsModalComponent.modalId]) {
        await this.showVariants();
      }
    });
  }

  private toggleRequiredValidators(controlNames: string[], required: boolean) {
    controlNames.forEach((name) => {
      const control = this.formControls[name as keyof typeof this.formControls];
      if (required) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity();
    });
  }

  private handleSuccessfulUpdate() {
    this.alerts.successSnackbarTR('PRODUCTS.PRODUCT_UPDATE_SUCCESS');
    this.router.navigate(['/main/vendors', this.vendorId, 'products', this.productId]);
    untracked(() => {
      this.eventBus.cast('product:fetch', { productId: this.productId });
    });
  }

  private updateFormWithProduct(product: ProductUpdateDtoMap) {
    this.editUtils.form.patchValue(product);
    const variations = product.variations ?? [];
    this.advancedMode.set(variations.length > 0);
    untracked(() => {
      this.attributesStore.setVariants(product);
    });
  }

  private createModalConfig(component: typeof AttributesModalComponent | typeof VariantsModalComponent) {
    return {
      component,
      componentProps: { attributesStore: this.attributesStore },
      expandToScroll: false,
      breakpoints: [0, 0.8],
      initialBreakpoint: 0.8,
      cssClass: 'rounded-dialog',
    } satisfies ModalOptions;
  }

  async updateAttributes() {
    const modal = await this.modalCtr.create(this.createModalConfig(AttributesModalComponent));
    await modal.present();
  }

  async showVariants() {
    const modal = await this.modalCtr.create(this.createModalConfig(VariantsModalComponent));
    await modal.present();
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  submit() {
    const form = this.editUtils.form;
    const product = this.store.productUpdateForDiff();

    if (!product) {
      this.handleSubmitError();
      return;
    }

    // Update variations BEFORE validation so they're included in the form state
    this.updateVariationsControl(form);

    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const updatedValues = this.editUtils.parse();
    if (!updatedValues.success) {
      this.handleSubmitError();
      form.markAllAsTouched();
      return;
    }

    if (this.hasPendingUploads()) {
      this.alerts.dangerSnackbarTR('COMMON.ATTACHMENTS_UPLOAD_IN_PROGRESS');
      return;
    }

    // Always get fresh product state for diff to avoid stale comparisons
    const freshProduct = this.store.productUpdateForDiff();
    if (!freshProduct) {
      this.handleSubmitError();
      return;
    }

    const operations = createPatch(freshProduct, updatedValues.data);
    if (operations.length === 0) {
      this.handleNoChanges();
      return;
    }
    this.store.productUpdate({ product: updatedValues.data }, { productId: this.productId });
  }

  private updateVariationsControl(form: typeof this.editUtils.form) {
    const variationsControl = form.controls.variations;
    const currentVariants = this.attributesStore.variants();
    variationsControl.setValue(currentVariants);
    // Only mark as dirty if the value actually changed
    if (currentVariants.length > 0) {
      variationsControl.markAsDirty();
    }
  }

  private hasPendingUploads(): boolean {
    return this.attachment()?.hasPendingUploads() === true;
  }

  private handleSubmitError() {
    this.store.setProductUpdateFailure(new LocalFailure());
  }

  private handleNoChanges() {
    this.router.navigate(['/main/vendors', this.vendorId, 'products', this.productId]);
    this.alerts.snackbarTR('COMMON.NO_CHANGES_MADE');
  }
}
